import type { ServicePageData } from './types';

const eventBoards: ServicePageData = {
  slug: 'event-boards',
  eyebrow: 'Signage',
  title: 'Get your message up fast with',
  titleAccent: 'printed event boards.',
  intro:
    'Foamex and Correx boards, A-frames and swing signs are the quickest, most affordable way to put a message where people will see it. We print, cut and finish everything in-house, ready to collect or delivered to site.',
  heroImage: '/home/pavement-signs/pavement-sign-mockup.jpg',

  useCases: [
    { title: 'Events and exhibitions', text: 'Directional and welcome boards that go up and come down in minutes.' },
    { title: 'Estate agents', text: 'For sale, sold and to let boards in the classic lightweight format.' },
    { title: 'Site boards', text: 'Planning notices, contractor details and site information boards.' },
    { title: 'Directional signage', text: 'Arrow boards and wayfinding for car parks, venues and open days.' },
    { title: 'Pavement advertising', text: 'A-frames and swing signs to catch passing footfall outside your door.' },
  ],

  optionsHeading: 'Choose your board',
  optionsIntro: 'Every board is printed and finished in our Kidderminster unit.',
  options: [
    {
      id: 'foamex',
      title: 'Foamex board',
      lead: 'Rigid, lightweight and smooth enough for full-colour print or vinyl.',
      body: 'A good all-rounder for interior use and short-term outdoor signage, easy to hang, screw or tape in place.',
      image: '/home/signboards/foamex-panel.jpg',
      specs: [
        { label: 'Size', value: 'Up to 8 x 4ft, or cut to any smaller size.' },
        { label: 'Material', value: 'Rigid PVC foam board.' },
        { label: 'Finish', value: 'Full-colour print, vinyl applied, or laminated on request.' },
        { label: 'Fixings', value: 'Screws, tape or stand-offs.' },
      ],
    },
    {
      id: 'correx',
      title: 'Correx board',
      lead: 'The classic estate-agent and site board material, waterproof and light.',
      body: 'The quickest, most affordable way to get a message up outdoors. Waterproof fluted polypropylene that is easy to cable-tie to a fence or fix with stakes.',
      image: '/home/signboards/correx-panel.jpg',
      specs: [
        { label: 'Size', value: 'Up to 8 x 4ft, or a standard estate-agent size.' },
        { label: 'Material', value: 'Fluted, waterproof polypropylene.' },
        { label: 'Finish', value: 'Full-colour print, single or double-sided.' },
        { label: 'Fixings', value: 'Cable ties, screws or stakes.' },
      ],
    },
    {
      id: 'a-frame',
      title: 'A-frame',
      lead: 'A self-standing pavement board that folds flat for storage.',
      body: 'Stands on its own without fixings, so it is out on the pavement in the morning and back inside at closing time. Graphics slot in on both sides.',
      image: '/home/pavement-signs/pavement-sign-mockup.jpg',
      specs: [
        { label: 'Size', value: 'A1 or A0 panel size.' },
        { label: 'Frame', value: 'Folding aluminium or steel A-frame.' },
        { label: 'Graphics', value: 'Foamex or Correx inserts, changeable when your message updates.' },
        { label: 'Use', value: 'Double-sided, readable from both directions.' },
      ],
    },
    {
      id: 'swing-sign',
      title: 'Swing sign',
      lead: 'A weighted pavement sign that turns with the wind instead of blowing over.',
      body: 'A more stable option than a lightweight A-frame for a spot that catches the wind, with a weighted base and a panel that swings rather than tips.',
      image: '/home/pavement-signs/swinger-pavement-sign-portrait.jpg',
      specs: [
        { label: 'Size', value: 'Portrait panel, standard swing-sign format.' },
        { label: 'Base', value: 'Weighted base for stability.' },
        { label: 'Graphics', value: 'Full-colour print, double-sided.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Photography, gradients and fine detail printed directly onto the board on our in-house wide-format printer.',
    },
    {
      title: 'Cut vinyl',
      text: 'Sharp-edged lettering and logos cut from coloured vinyl and applied to a plain board, well suited to simple estate-agent and directional signage.',
    },
  ],

  gallery: [
    '/home/pavement-signs/pavement-sign-mockup.jpg',
    '/home/pavement-signs/pavement-sign-wildjac.jpg',
    '/home/pavement-signs/pavement-sign-atelier.jpg',
    '/home/pavement-signs/swinger-pavement-sign-portrait.jpg',
    '/home/pavement-signs/pavement-sign-hkh.jpg',
    '/home/pavement-signs/pavement-sign-tattoo.jpg',
    '/home/pavement-signs/pavement-sign-petalbar.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Boards are cut, printed and ready to fix, with instructions included for any frame or stand.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Installed',
      text: 'Our insured install team can put up site and directional boards for you where access or scale makes a self-install impractical.',
    },
    {
      title: 'Turnaround',
      text: '5 to 10 working days from approval. Got an event date to hit? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'What is the difference between Foamex and Correx?',
      a: 'Foamex is a smooth rigid board suited to interior use and short-term outdoor signage. Correx is a cheaper, waterproof fluted board better suited to site boards and longer outdoor exposure.',
    },
    {
      q: 'Do you supply the A-frame or swing-sign stand as well as the graphic?',
      a: 'Yes, we can supply the full stand with your printed panel, or just a replacement panel if you already have a frame.',
    },
    {
      q: 'Can I reuse the frame and just order new graphics later?',
      a: 'Yes, A-frame and swing-sign panels are designed to be swapped out on their own when your message changes.',
    },
  ],

  cta: {
    title: 'Get your free event board quote.',
    text: 'Tell us what you need boards for and we will recommend the right material and format.',
    image: '/home/pavement-signs/pavement-sign-mockup.jpg',
  },
};

export default eventBoards;
